import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, mergeMap, scan } from 'rxjs/operators';
import { HttpRequestMethods } from '../../../../constants/utils/api.constants';
import {
  HttpHeadersPayload,
  ReplaceItem
} from '../../../../models/utils/common.interfaces';
export class ApiHelpers {
  protected _queryParams!: string;
  protected _override = false;
  protected _overrideEndpointValues: ReplaceItem[] = [];
  protected _library = 'no-library-defined';
  protected _withCredentials = false;
  protected _retries = 0;
  /** Set the Http request headers */
  queryParams(payload: string) {
    this._queryParams = payload;
    return this;
  }

  /** Indicate if the endpoint will be overridden or updated */
  overrideEndpoint(values: ReplaceItem[]) {
    this._override = true;
    this._overrideEndpointValues = values;
    return this;
  }

  /** Indicate the library where the service is being used */
  library(name: string) {
    this._library = name;
    return this;
  }

  /** Indicate the times the service will retry it*/
  retries(count: number) {
    this._retries = count;
    return this;
  }

  withCredentials(value: boolean) {
    this._withCredentials = value;
    return this;
  }

  /** Override the endpoint */
  protected _overrideEndpoint(url: string): string {
    this._override = false;
    this._overrideEndpointValues.forEach((key: ReplaceItem) => {
      url = url.replace(key.src, key.target);
    });
    return url;
  }

  protected _createHttpHeaders(
    payload: HttpHeadersPayload | undefined
  ): HttpHeaders {
    return new HttpHeaders(payload);
  }

  /** A mapper function that return the full response of a call*/
  protected _httpFullResponseMapper<Request, Response>(
    url: string,
    method: HttpRequestMethods,
    httpClient: HttpClient,
    payload?: HttpHeadersPayload,
    requestObject?: Request
  ): Observable<Response> {
    switch (method) {
      case HttpRequestMethods.POST_FULL_RESPONSE:
        return httpClient
          .post<Response>(url, requestObject, {
            observe: 'response',
            headers: this._createHttpHeaders(payload),
            withCredentials: this._withCredentials
          })
          .pipe(
            map((response) => {
              return response as unknown as Response;
            })
          );
      case HttpRequestMethods.GET_FULL_RESPONSE:
        return httpClient
          .get<Response>(url, {
            observe: 'response',
            headers: this._createHttpHeaders(payload),
            withCredentials: this._withCredentials
          })
          .pipe(
            map((response) => {
              return response as unknown as Response;
            })
          );
      default:
        return this._httpPutResponseMapper<Request, Response>(
          url,
          method,
          httpClient,
          payload,
          requestObject
        );
    }
  }

  /** A mapper function that return the PUT response of a call*/
  protected _httpPutResponseMapper<Request, Response>(
    url: string,
    method: HttpRequestMethods,
    httpClient: HttpClient,
    payload?: HttpHeadersPayload,
    requestObject?: Request
  ): Observable<Response> {
    switch (method) {
      case HttpRequestMethods.PUT:
        return httpClient.put<Response>(url, requestObject, {
          headers: this._createHttpHeaders(payload)
        });
      case HttpRequestMethods.PUT_FULL_RESPONSE:
        return httpClient
          .put<Response>(url, requestObject, {
            observe: 'response',
            headers: this._createHttpHeaders(payload)
          })
          .pipe(
            map((response) => {
              return response as unknown as Response;
            })
          );
      default:
        return of();
    }
  }

  protected _retryWhen(error: Observable<HttpErrorResponse>) {
    return error.pipe(
      mergeMap((error) => {
        if (error.status == 429) return of(error);
        return throwError(error);
      }),
      scan((accumulator, error) => {
        if (accumulator > this._retries) throw error;
        return accumulator + 1;
      }, 1)
    );
  }
}
